'use client';

/**
 * OrganizationDetailsTab
 *
 * Details tab content for organization page.
 * Shows editable form for name, slug, billing email, and logo.
 */

import {
  Alert,
  AlertDescription,
  Button,
  ImageUpload,
  Inline,
  Input,
  Label,
  OrgLogo,
  Stack,
  Text,
} from '@cortex/ui';
import { useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  billing_email: string | null;
}

interface OrganizationDetailsTabProps {
  organization: Organization;
  onUpdate: () => void;
}

export function OrganizationDetailsTab({
  organization,
  onUpdate,
}: OrganizationDetailsTabProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [name, setName] = useState(organization.name);
  const [slug, setSlug] = useState(organization.slug);
  const [billingEmail, setBillingEmail] = useState(
    organization.billing_email || ''
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(organization.logo_url);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleLogoUpload = async (file: File) => {
    setUploadingLogo(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetchWithAuth(
        `/admin/organizations/${organization.id}/logo`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to upload logo');
      }

      const { data } = await response.json();
      setLogoUrl(data.logo_url);
      setSuccess('Logo uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    setDeletingLogo(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `/admin/organizations/${organization.id}/logo`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete logo');
      }

      setLogoUrl(null);
      setSuccess('Logo deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete logo');
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetchWithAuth(
        `/admin/organizations/${organization.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            slug,
            billingEmail: billingEmail || null,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update organization');
      }

      setSuccess('Organization updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      onUpdate();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update organization'
      );
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    name !== organization.name ||
    slug !== organization.slug ||
    billingEmail !== (organization.billing_email || '');

  return (
    <Stack gap="lg">
      {/* Error/Success Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Logo Upload */}
      <Stack gap="xs">
        <Label className="mb-2">Logo</Label>
        <Inline gap="md" align="start">
          <ImageUpload
            src={logoUrl}
            placeholder={<OrgLogo name={name || 'Organization'} size="lg" />}
            alt={`${name} logo`}
            onUpload={handleLogoUpload}
            onDelete={logoUrl ? handleLogoDelete : undefined}
            uploading={uploadingLogo}
            deleting={deletingLogo}
            size="lg"
          />
          <Stack className="flex-1" gap="none">
            <Text color="muted" size="sm">
              Upload a square logo (256x256 recommended).
            </Text>
            <Text color="muted" size="xs" className="mt-1">
              PNG, JPEG, or WebP. Max 2MB.
            </Text>
          </Stack>
        </Inline>
      </Stack>

      {/* Organization Name */}
      <Stack gap="xs">
        <Label htmlFor="orgName">Organization Name</Label>
        <Input
          id="orgName"
          type="text"
          placeholder="Acme Corporation"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1"
        />
      </Stack>

      {/* Slug */}
      <Stack gap="xs">
        <Label htmlFor="orgSlug">Slug</Label>
        <Input
          id="orgSlug"
          type="text"
          placeholder="acme-corporation"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="mt-1"
        />
        <Text color="muted" size="xs" className="mt-1">
          URL-friendly identifier. Only lowercase letters, numbers, and hyphens.
        </Text>
      </Stack>

      {/* Billing Email */}
      <Stack gap="xs">
        <Label htmlFor="billingEmail">Billing Email</Label>
        <Input
          id="billingEmail"
          type="email"
          placeholder="billing@example.com"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          className="mt-1"
        />
        <Text color="muted" size="xs" className="mt-1">
          Email address for billing and invoices.
        </Text>
      </Stack>

      {/* Save Button */}
      <Inline justify="end">
        <Button onClick={handleSave} disabled={!hasChanges} loading={saving}>
          Save Changes
        </Button>
      </Inline>
    </Stack>
  );
}
